import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarBlank, CaretDown, Trophy } from "@phosphor-icons/react";
import { SportLeagueNav } from "../components/dashboard/SportLeagueNav";
import { GameCard } from "../components/dashboard/GameCard";
import { NflPreviewCard } from "../components/dashboard/NflPreviewCard";
import { NflPerformancePanel } from "../components/dashboard/NflPerformancePanel";
import { NbaPreviewCard } from "../components/dashboard/NbaPreviewCard";
import { NbaLeansAndResultsPanel } from "../components/dashboard/NbaLeansAndResultsPanel";
import { BestBetsPanel } from "../components/dashboard/BestBetsPanel";
import { RecentResultsPanel } from "../components/dashboard/RecentResultsPanel";
import { PipelineStatusStrip } from "../components/dashboard/PipelineStatusStrip";
import { EmptyState } from "../components/shared/EmptyState";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { getGames, getGamePrediction, getTeams, getNflPreview, getNbaPreview, formatApiErrorDetail } from "../lib/api";
import { LEAGUES } from "../constants/leagues";
import { SPORTS, LEAGUES_BY_SPORT } from "../constants/sports";

// Same 58% bar the game cards already use to flag a strong lean -- Best Bets is just today's
// slate filtered down to those leans and sorted by confidence, never a separately-invented score.
const CONFIDENCE_THRESHOLD = 0.58;
const MAX_BEST_BETS = 5;
const DEFAULT_SPORT = "baseball";
const lastLeagueKey = (sport) => `edgeball:lastLeague:${sport}`;

const resolveLeagueForSport = (sport, requested) => {
  const leagueCodes = LEAGUES_BY_SPORT[sport] || [];
  if (requested && leagueCodes.includes(requested)) return requested;
  const stored = window.localStorage.getItem(lastLeagueKey(sport));
  if (stored && leagueCodes.includes(stored)) return stored;
  return leagueCodes[0];
};

function computeBestBets(games, predictions) {
  const entries = [];
  for (const game of games) {
    const p = predictions[game.id];
    if (!p) continue;
    const awayName = game.away_team?.name || game.away_team_id || "TBD";
    const homeName = game.home_team?.name || game.home_team_id || "TBD";

    const total = p.total;
    if (total?.status === "ok" && total.over_probability != null && total.under_probability != null && total.market_line?.line_value != null) {
      const overFavored = total.over_probability >= total.under_probability;
      const confidence = overFavored ? total.over_probability : total.under_probability;
      if (confidence >= CONFIDENCE_THRESHOLD) {
        entries.push({
          gameId: game.id,
          awayName,
          homeName,
          market: "O/U",
          pick: `${overFavored ? "Over" : "Under"} ${total.market_line.line_value}`,
          confidence,
        });
      }
    }
  }
  return entries.sort((a, b) => b.confidence - a.confidence).slice(0, MAX_BEST_BETS);
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// NBA "today" matches US Eastern (how the backend/schedule assigns a game's own date) -- a
// plain UTC-based date would show tomorrow's slate for US users late in the evening.
const nbaTodayEt = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeSport, setActiveSport] = useState(() =>
    SPORTS.some((s) => s.enabled && s.code === searchParams.get("sport")) ? searchParams.get("sport") : DEFAULT_SPORT
  );
  const [activeLeague, setActiveLeague] = useState(() => resolveLeagueForSport(activeSport, searchParams.get("league")));
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [predictionsLoading, setPredictionsLoading] = useState(true);
  const [nflPreview, setNflPreview] = useState(null);
  const [nflError, setNflError] = useState(null);
  const [nflDetailsOpen, setNflDetailsOpen] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  const isNfl = activeLeague === "NFL";

  const todayIso = new Date().toISOString().slice(0, 10);

  const [nbaPreview, setNbaPreview] = useState(null);
  const [nbaError, setNbaError] = useState(null);
  const [nbaDetailsOpen, setNbaDetailsOpen] = useState(false);
  const [nbaDate, setNbaDate] = useState(nbaTodayEt);
  // Separate from nbaDate: the raw, possibly-incomplete value the native <input type="date">
  // reports on every keystroke (typing a year digit-by-digit fires onChange repeatedly with
  // garbage intermediate values) -- debounced + validated below before it ever becomes nbaDate.
  const [nbaDateInput, setNbaDateInput] = useState(nbaTodayEt);
  const isNba = activeLeague === "NBA";

  // Deep-link the initial selection even when the URL arrived without sport/league params.
  useEffect(() => {
    setSearchParams({ sport: activeSport, league: activeLeague }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSportChange = (sport) => {
    const league = resolveLeagueForSport(sport, null);
    setActiveSport(sport);
    setActiveLeague(league);
    setSearchParams({ sport, league });
  };

  const handleLeagueChange = (league) => {
    setActiveLeague(league);
    window.localStorage.setItem(lastLeagueKey(activeSport), league);
    setSearchParams({ sport: activeSport, league });
  };

  // NFL is an experimental, separately-sourced preview (services/prediction/nfl_model.py via
  // GET /api/nfl/preview) -- never mixed with the baseball games/predictions fetch below.
  useEffect(() => {
    if (!isNfl) return undefined;
    setNflPreview(null);
    setNflError(null);
    setLoading(true);
    getNflPreview()
      .then((data) => setNflPreview(data))
      .catch((err) => setNflError(err?.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "Couldn't load the NFL preview. Please try again shortly."))
      .finally(() => setLoading(false));
  }, [isNfl]);

  // Debounces + validates the raw date-input value before it drives a fetch -- ignores
  // incomplete/garbage intermediate values from typing (e.g. "0001-10-30" mid-keystroke).
  useEffect(() => {
    if (!DATE_RE.test(nbaDateInput)) return undefined;
    const t = setTimeout(() => setNbaDate(nbaDateInput), 400);
    return () => clearTimeout(t);
  }, [nbaDateInput]);

  // NBA is an experimental, separately-sourced preview (services/prediction/nba_model.py via
  // GET /api/nba/preview) -- date selector drives a server-side rebuild, defaults to today.
  useEffect(() => {
    if (!isNba) return undefined;
    let active = true;
    setNbaPreview(null);
    setNbaError(null);
    setLoading(true);
    getNbaPreview(nbaDate)
      .then((data) => {
        if (active) setNbaPreview(data);
      })
      .catch((err) => {
        if (active) setNbaError(err?.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "Couldn't load the NBA preview. Please try again shortly.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isNba, nbaDate]);

  useEffect(() => {
    if (isNfl || isNba) return undefined;
    // Clear immediately on league switch so the previous league's games/leans never flash
    // while the new league's data is still loading.
    setGames([]);
    setTeamCount(null);
    setGamesError(null);
    setLoading(true);
    getTeams({ league: activeLeague })
      .then((data) => setTeamCount((data || []).length))
      .catch(() => setTeamCount(0));
    getGames({ league: activeLeague, date: todayIso })
      .then((data) => setGames(data || []))
      .catch((err) => {
        setGames([]);
        // A real request failure (network/5xx/etc.) must never look identical to "no games
        // today" -- surface it explicitly instead of silently swallowing it into an empty list.
        setGamesError(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load today's schedule. Please try again shortly.");
      })
      .finally(() => setLoading(false));
  }, [activeLeague, todayIso, isNfl, isNba]);

  // One shared fetch per game feeds both each GameCard's ML/O-U preview and the Best Bets panel.
  useEffect(() => {
    if (isNfl || isNba || games.length === 0) {
      setPredictions({});
      setPredictionsLoading(false);
      return undefined;
    }
    let active = true;
    setPredictionsLoading(true);
    Promise.all(
      games.map((game) =>
        getGamePrediction(game.id)
          .then((data) => [game.id, data])
          .catch(() => [game.id, null])
      )
    )
      .then((entries) => active && setPredictions(Object.fromEntries(entries)))
      .finally(() => active && setPredictionsLoading(false));
    return () => {
      active = false;
    };
  }, [games, isNfl, isNba]);

  const bestBets = useMemo(() => (isNfl || isNba ? [] : computeBestBets(games, predictions)), [games, predictions, isNfl, isNba]);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const leagueLabel = LEAGUES.find((l) => l.code === activeLeague)?.label || activeLeague;
  const isConnected = teamCount === null ? null : teamCount > 0;

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6">
      <SportLeagueNav activeSport={activeSport} activeLeague={activeLeague} onSportChange={handleSportChange} onLeagueChange={handleLeagueChange} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-heading text-2xl uppercase tracking-tight text-white">Model Projections</h1>
              <p className="flex items-center gap-1.5 font-mono text-xs text-zinc-500">
                <CalendarBlank size={14} />
                {isNfl && nflPreview && nflPreview.week ? (
                  <span data-testid="nfl-week-header">
                    {`${leagueLabel} \u00b7 ${nflPreview.season} ${nflPreview.game_type === "REG" ? "Regular Season" : "Postseason"} Week ${nflPreview.week} \u00b7 ${nflPreview.week_date_range.start} to ${nflPreview.week_date_range.end}`}
                  </span>
                ) : isNba ? (
                  <span data-testid="nba-date-header">
                    {leagueLabel} &middot; {nbaPreview?.season ? `${nbaPreview.season - 1}-${String(nbaPreview.season).slice(2)} season` : "season"}
                  </span>
                ) : (
                  <span>
                    {today} &middot; {leagueLabel}
                  </span>
                )}
              </p>
            </div>
            {isNba && (
              <input
                data-testid="nba-date-picker"
                type="date"
                value={nbaDateInput}
                onChange={(e) => setNbaDateInput(e.target.value)}
                className="rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs text-zinc-300 outline-none focus:border-eb-accent/40"
              />
            )}
          </div>

          {activeLeague === "NPB" && (
            <p data-testid="npb-market-only-notice" className="mb-3 rounded-lg border border-white/10 bg-eb-surface/60 px-3 py-2 font-mono text-xs text-zinc-500">
              Experimental: schedule and bookmaker totals are live for NPB. Model projections and win probabilities aren&rsquo;t
              shown yet &mdash; real stat inputs (starter/bullpen quality) aren&rsquo;t connected for this league.
            </p>
          )}

          {isNfl && nflPreview && (
            <Collapsible open={nflDetailsOpen} onOpenChange={setNflDetailsOpen} className="mb-3">
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-eb-surface/60 px-3 py-2">
                <span className="rounded-full border border-eb-accent/40 bg-eb-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-eb-accent">
                  Experimental model
                </span>
                <CollapsibleTrigger
                  data-testid="nfl-model-details-toggle"
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-zinc-500 transition-colors hover:text-zinc-300"
                >
                  Model details <CaretDown size={10} className={`transition-transform ${nflDetailsOpen ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent data-testid="nfl-model-details-content" className="mt-2 flex flex-col gap-2 rounded-lg border border-white/10 bg-eb-surface/60 px-3 py-3 font-mono text-[11px] leading-relaxed text-zinc-500">
                <p>{nflPreview.notice}</p>
                <p className="text-zinc-600">{nflPreview.probability_method}</p>
                <p className="border-t border-white/5 pt-2 text-[10px] text-zinc-600">
                  Cache: schedule {nflPreview.cache_freshness.schedule.fetched_at?.slice(0, 16).replace("T", " ")} UTC &middot; injuries/depth charts/rosters{" "}
                  {nflPreview.cache_freshness.injuries_2026?.fetched_at?.slice(0, 16).replace("T", " ")} UTC
                  {nflPreview.last_refresh_job && nflPreview.last_refresh_job.status !== "never_run" && (
                    <span data-testid="nfl-refresh-job-status" className={nflPreview.last_refresh_job.status === "ok" ? "ml-2 text-zinc-600" : "ml-2 text-yellow-500"}>
                      &middot; last scheduled refresh: {nflPreview.last_refresh_job.status} ({nflPreview.last_refresh_job.finished_at?.slice(0, 16).replace("T", " ")} UTC)
                    </span>
                  )}
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}

          {isNba && nbaPreview && (
            <Collapsible open={nbaDetailsOpen} onOpenChange={setNbaDetailsOpen} className="mb-3">
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-eb-surface/60 px-3 py-2">
                <span className="rounded-full border border-eb-accent/40 bg-eb-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-eb-accent">
                  Experimental model
                </span>
                <CollapsibleTrigger
                  data-testid="nba-model-details-toggle"
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-zinc-500 transition-colors hover:text-zinc-300"
                >
                  Model details <CaretDown size={10} className={`transition-transform ${nbaDetailsOpen ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent data-testid="nba-model-details-content" className="mt-2 flex flex-col gap-2 rounded-lg border border-white/10 bg-eb-surface/60 px-3 py-3 font-mono text-[11px] leading-relaxed text-zinc-500">
                <p>{nbaPreview.notice}</p>
                <p className="text-zinc-600">{nbaPreview.probability_method}</p>
                {nbaPreview.odds_status?.stale && (
                  <p data-testid="nba-odds-stale-notice" className="text-yellow-500">
                    Bookmaker odds are currently unavailable{nbaPreview.odds_status.last_error ? ` (${nbaPreview.odds_status.last_error})` : ""} &mdash; moneyline/spread/total
                    probabilities above still show the model's own projection, just without a matched book line.
                  </p>
                )}
                <p className="border-t border-white/5 pt-2 text-[10px] text-zinc-600">
                  Cache: schedule {nbaPreview.cache_freshness.schedule.fetched_at?.slice(0, 16).replace("T", " ")} UTC
                  {nbaPreview.last_refresh_job && nbaPreview.last_refresh_job.status !== "never_run" && (
                    <span data-testid="nba-refresh-job-status" className={nbaPreview.last_refresh_job.status === "ok" ? "ml-2 text-zinc-600" : "ml-2 text-yellow-500"}>
                      &middot; last scheduled refresh: {nbaPreview.last_refresh_job.status} ({nbaPreview.last_refresh_job.finished_at?.slice(0, 16).replace("T", " ")} UTC)
                    </span>
                  )}
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}

          <div data-testid="games-list" className="flex flex-col gap-3">
            {isNfl ? (
              loading ? (
                <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
                  Loading NFL preview&hellip;
                </div>
              ) : nflError ? (
                <EmptyState icon={Trophy} title="Preview Unavailable" description={nflError} testId="empty-state-nfl-error" />
              ) : nflPreview && nflPreview.games.length > 0 ? (
                nflPreview.games.map((game) => <NflPreviewCard key={game.game_id} game={game} />)
              ) : (
                <EmptyState icon={Trophy} title="No Games This Week" description="No current or upcoming NFL week with scheduled games was found in the cached schedule." testId="empty-state-nfl-no-games" />
              )
            ) : isNba ? (
              loading ? (
                <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
                  Loading NBA preview&hellip;
                </div>
              ) : nbaError ? (
                <EmptyState icon={Trophy} title="Preview Unavailable" description={nbaError} testId="empty-state-nba-error" />
              ) : nbaPreview && nbaPreview.games.length > 0 ? (
                nbaPreview.games.map((game) => <NbaPreviewCard key={game.game_id} game={game} />)
              ) : (
                <EmptyState icon={Trophy} title="No Games This Day" description="No NBA games were found on the cached schedule for this date." testId="empty-state-nba-no-games" />
              )
            ) : loading ? (
              <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
                Loading schedule&hellip;
              </div>
            ) : games.length > 0 ? (
              games.map((game) => <GameCard key={game.id} game={game} prediction={predictions[game.id]} />)
            ) : gamesError ? (
              <EmptyState icon={Trophy} title="Couldn't Load Schedule" description={gamesError} testId="empty-state-games-error" />
            ) : isConnected === false ? (
              <EmptyState
                icon={Trophy}
                title="League Not Connected Yet"
                description={`${leagueLabel} game data isn't connected yet. Check back once a data source is added.`}
                testId="empty-state-league-not-connected"
              />
            ) : (
              <EmptyState
                icon={Trophy}
                title="No Games Scheduled"
                description={`No ${leagueLabel} games are scheduled for today.`}
                testId="empty-state-todays-games"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-4">
          {!isNfl && !isNba && <BestBetsPanel bets={bestBets} loading={loading || predictionsLoading} />}
          {!isNfl && !isNba && <RecentResultsPanel />}
          {!isNfl && !isNba && <PipelineStatusStrip />}
          {isNfl && <NflPerformancePanel />}
          {isNba && <NbaLeansAndResultsPanel />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
